/**
 * Fixed position of a popup anchored to an element (menus, combobox lists):
 * below the anchor, or above it when there is not enough room; left- or
 * right-aligned; optionally as wide as the anchor. Popups are portaled to
 * <body> so a scrolling grid, drawer body or dialog never clips them; the
 * position follows scroll (any ancestor) and resize while open.
 *
 * @param {React.RefObject<HTMLElement>} anchorRef
 * @param {boolean} open
 * @param {{align?: "start"|"end", matchWidth?: boolean, minRoom?: number}} [opts]
 * @returns {null | {top: number, left?: number, right?: number, width?: number, maxHeight: number, up: boolean}}
 */
import * as React from "react";

/** useLayoutEffect in the browser, useEffect on the server (no SSR warning). */
const useIsoLayoutEffect = typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

export function anchorPosition(rect, viewport, { align = "start", matchWidth = false, minRoom = 200 } = {}) {
  const below = viewport.height - rect.bottom - 8;
  const above = rect.top - 8;
  const up = below < minRoom && above > below;
  const pos = {
    top: up ? rect.top - 4 : rect.bottom + 4,
    maxHeight: Math.max(160, (up ? above : below) - 4),
    up,
  };
  if (align === "end") pos.right = Math.max(8, viewport.width - rect.right);
  else pos.left = Math.max(8, rect.left);
  if (matchWidth) pos.width = Math.round(rect.width);
  return pos;
}

export function useAnchorPosition(anchorRef, open, opts = {}) {
  const [pos, setPos] = React.useState(null);
  const { align, matchWidth, minRoom } = opts;
  const place = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    setPos(
      anchorPosition(
        el.getBoundingClientRect(),
        { width: window.innerWidth, height: window.innerHeight },
        { align, matchWidth, minRoom },
      ),
    );
  }, [anchorRef, align, matchWidth, minRoom]);
  useIsoLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);
  return open ? pos : null;
}

/** Inline style of a positioned popup. */
export function anchorStyle(pos) {
  if (!pos) return undefined;
  return {
    top: pos.top,
    left: pos.left,
    right: pos.right,
    width: pos.width,
    maxHeight: pos.maxHeight,
    transform: pos.up ? "translateY(-100%)" : undefined,
  };
}

export default useAnchorPosition;
